import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Loader from '../../shared/components/ui/Loader';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import {
    getManagementTaskById,
    acceptManagementTaskOwnerTransfer,
    cancelManagementTaskOwnerTransfer
} from '../services/api/implementation/impl';
import noDataIcon from '../../shared/assets/svg/NoData.svg';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import AddDocumentModal from './Management/AddDocumentModal';
import CreateSubtaskModal from './Management/CreateSubtaskModal';
import TaskActivityPanel, { ACTIVITY_TABS } from './Management/TaskActivityPanel';
import TaskHistoryPanel from './Management/TaskHistoryPanel';
import PermissionPanel from './Management/PermissionPanel';
import QueryRoundsPanel from './Management/QueryRoundsPanel';
import OwnerTransferBanner, { pendingOwnerTransfer } from './Management/OwnerTransferBanner';
import TaskBadges from './Management/TaskBadges';
import ReviewActions from './Management/ReviewActions';
import AssigneeKindChip from './Management/AssigneeKindChip';
import ManagementTaskSummaryCard from './Management/ManagementTaskSummaryCard';
import SubtaskReviewNotes from './Management/SubtaskReviewNotes';
import { statusChipClass, TaskStatus } from './Management/taskWorkflow';
import { nestSubtasks, reviewerId, subtaskStatusLabel, allSubtasksClosed } from './Management/taskTree';
import {
    kindLabel,
    usesPermissionFlow,
    subtaskTypeChipClass,
    SubTaskPermissionType
} from './Management/permission';
import UserAvatar from '../../shared/components/ui/UserAvatar';

const OperationsManagementTaskUpdates = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { collapsed } = useSidebar();

    const [isAddDocModalOpen, setIsAddDocModalOpen] = React.useState(false);
    const [activityTargetId, setActivityTargetId] = React.useState(null);
    // A new query ({ newQuery: true }) or a reply to an existing one.
    const [queryPrefill, setQueryPrefill] = React.useState(null);

    const queryClient = useQueryClient();

    const { data: taskData, isLoading } = useQuery({
        queryKey: ['managementTask', id],
        queryFn: () => getManagementTaskById(id),
        enabled: !!id
    });

    const task = taskData?.data;
    const pending = pendingOwnerTransfer(task);
    const overviewSubtasks = (() => {
        if (!task) return [];
        const userId = Cookies.get('userId');
        return nestSubtasks(task.subTask)
            .filter((subtask) => {
                const assignedToMe = String(subtask.assignedTo_id) === String(userId);
                const forMyReview =
                    String(reviewerId(task, subtask)) === String(userId) &&
                    (subtask.status === TaskStatus.UNDER_REVIEW ||
                        subtask.status === TaskStatus.COMPLETED);
                return assignedToMe || forMyReview;
            })
            .sort((a, b) => {
                const rank = (row) => {
                    if (row.status === TaskStatus.UNDER_REVIEW) return 0;
                    if (row.status === TaskStatus.COMPLETED) return 2;
                    return 1;
                };
                return rank(a) - rank(b);
            });
    })();
    const showActionColumn = !allSubtasksClosed(overviewSubtasks);
    // Named for the kind, so a compliance task never shows a Permission tab.
    const flowTab = kindLabel(task?.kind);
    const sectionTabs = [
        'Overview',
        'History',
        ...(usesPermissionFlow(task) ? [flowTab, 'Query rounds'] : []),
        ...ACTIVITY_TABS
    ];
    const sectionParam = searchParams.get('section');
    const activeSection = sectionTabs.find(
        (name) => name.toLowerCase() === String(sectionParam || 'overview').toLowerCase()
    ) || 'Overview';

    const setSection = (name) => {
        const next = new URLSearchParams(searchParams);
        if (!name || name === 'Overview') {
            next.delete('section');
        } else {
            next.set('section', name.toLowerCase());
            next.delete('tab');
        }
        setSearchParams(next, { replace: true });
    };

    const invalidateTask = () => {
        queryClient.invalidateQueries(['managementTask', id]);
        queryClient.invalidateQueries(['managementTasks']);
    };

    const acceptMutation = useMutation({
        mutationFn: (transferId) => acceptManagementTaskOwnerTransfer(id, transferId),
        onSuccess: () => {
            toast.success('You are now the owner');
            invalidateTask();
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to accept transfer');
        }
    });

    const cancelMutation = useMutation({
        mutationFn: (transferId) => cancelManagementTaskOwnerTransfer(id, transferId),
        onSuccess: () => {
            toast.success('Owner transfer cancelled');
            invalidateTask();
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to cancel transfer');
        }
    });

    const formatDate = (dateString) => {
        if (!dateString) return '---';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <>
            <Loader isVisible={isLoading} />

            {isAddDocModalOpen && activityTargetId && (
                <AddDocumentModal
                    subtaskId={activityTargetId}
                    existingDocs={task?.subTask?.find(st => st.id === activityTargetId)?.docs || []}
                    onClose={() => setIsAddDocModalOpen(false)}
                />
            )}
            {queryPrefill && task && (
                <CreateSubtaskModal
                    onClose={() => setQueryPrefill(null)}
                    parentTask={task}
                    initialTitle={queryPrefill.title}
                    initialDescription={queryPrefill.description}
                    initialPermissionType={SubTaskPermissionType.QUERY_RAISED}
                    initialPermissionQueryId={queryPrefill.permissionQueryId}
                    newQuery={queryPrefill.newQuery}
                />
            )}
            <Layout>
                <div
                    className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
                        }`}>
                    <Header backArrow={true} data={{ name: 'Task Details' }} />
                </div>

                <div className="mt-[6rem] px-6 pb-10">
                    {task ? (
                        <div className="space-y-6">
                            <ManagementTaskSummaryCard
                                task={task}
                                showBudget={activeSection === 'Overview'}
                            />

                            <div className="border-b border-gray-200">
                                <div className="flex items-center gap-5 overflow-x-auto whitespace-nowrap">
                                    {sectionTabs.map((name) => (
                                        <button
                                            key={name}
                                            type="button"
                                            onClick={() => setSection(name)}
                                            className={`text-base pb-2.5 ${
                                                name === activeSection
                                                    ? 'text-teal-700 border-b-2 border-teal-700 font-semibold'
                                                    : 'text-gray-700 font-medium hover:text-gray-900'
                                            }`}>
                                            {name}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <OwnerTransferBanner
                                task={task}
                                onCancel={() => pending && cancelMutation.mutate(pending.id)}
                                onAccept={() => pending && acceptMutation.mutate(pending.id)}
                                onDecline={() => pending && cancelMutation.mutate(pending.id)}
                                busy={acceptMutation.isPending || cancelMutation.isPending}
                            />

                            {activeSection === 'History' && <TaskHistoryPanel task={task} />}

                            {activeSection === flowTab && (
                                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5">
                                    <PermissionPanel task={task} embedded />
                                </div>
                            )}

                            {activeSection === 'Query rounds' && (
                                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5">
                                    <QueryRoundsPanel
                                        task={task}
                                        embedded
                                        onAddQuery={() =>
                                            setQueryPrefill({
                                                title: `Reply to query #${(task.permissionQueries || []).length + 1}`,
                                                description: '',
                                                permissionQueryId: '',
                                                newQuery: true
                                            })
                                        }
                                        onCreateReply={(query, index) =>
                                            setQueryPrefill({
                                                title: `Reply to query #${index + 1}`,
                                                description: query?.text || '',
                                                permissionQueryId: query?.id || '',
                                                newQuery: false
                                            })
                                        }
                                    />
                                </div>
                            )}

                            {ACTIVITY_TABS.includes(activeSection) && (
                                <TaskActivityPanel
                                    task={task}
                                    tab={activeSection}
                                    targetId={activityTargetId}
                                    onTargetChange={setActivityTargetId}
                                    onOpenAddDoc={(subtaskId) => {
                                        setActivityTargetId(subtaskId);
                                        setIsAddDocModalOpen(true);
                                    }}
                                />
                            )}

                            {activeSection === 'Overview' && (
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="overflow-x-auto overflow-y-hidden">
                                <table className="min-w-full text-left">
                                    <thead className="bg-gray-50 text-gray-600">
                                        <tr>
                                            <th className="px-6 py-3 font-medium text-sm">Title</th>
                                            <th className="px-6 py-3 font-medium text-sm">Description</th>
                                            <th className="px-6 py-3 font-medium text-sm">Assigned To</th>
                                            <th className="px-6 py-3 font-medium text-sm">Approver</th>
                                            <th className="px-6 py-3 font-medium text-sm">Status</th>
                                            <th className="px-6 py-3 font-medium text-sm">Dates</th>
                                            {showActionColumn && (
                                                <th className="px-6 py-3 font-medium text-sm">Action</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {overviewSubtasks.length > 0 ? (
                                                overviewSubtasks.map((subtask) => (
                                                    <tr
                                                        key={subtask.id}
                                                        className="hover:bg-gray-50"
                                                    >
                                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                            {subtask.title}
                                                            <SubtaskReviewNotes subtask={subtask} />
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{subtask.description}</td>
                                                        <td className="px-6 py-4 text-sm text-gray-700">
                                                            <div className="flex items-center gap-2">
                                                                <UserAvatar name={subtask.assignedTo?.name} size={28} />
                                                                <AssigneeKindChip kind={subtask.assigneeKind} name={subtask.assignedTo?.name} />
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-700">
                                                            <div className="flex items-center gap-2">
                                                                {/* No approver: the owner reviews it (see reviewerId). */}
                                                                <UserAvatar name={subtask.approver?.name || task.owner?.name} size={28} />
                                                                <span>{subtask.approver?.name || task.owner?.name || '---'}</span>
                                                                {!subtask.approver?.name && task.owner?.name && (
                                                                    <span className="text-xs text-gray-400">(Owner)</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className={`px-2 py-1 rounded text-xs ${statusChipClass(subtaskStatusLabel(task, subtask))}`}>
                                                                {subtaskStatusLabel(task, subtask)}
                                                            </span>
                                                            {usesPermissionFlow(task) && subtask.permissionType && (
                                                                <span className={`ml-2 px-2 py-1 rounded text-xs ${subtaskTypeChipClass(subtask.permissionType)}`}>
                                                                    {subtask.permissionType}
                                                                </span>
                                                            )}
                                                            <TaskBadges badges={subtask.badges} />
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-500">
                                                            <div className="flex flex-col text-xs">
                                                                <span>Start: {formatDate(subtask.plannedStartDate)}</span>
                                                                <span>End: {formatDate(subtask.plannedEndDate)}</span>
                                                                <span>Duration: {subtask.duration} day{subtask.duration === 1 ? '' : 's'}</span>
                                                            </div>
                                                        </td>
                                                        {showActionColumn && (
                                                        <td className="px-6 py-4">
                                                            <ReviewActions
                                                                task={task}
                                                                subtask={subtask}
                                                                onDone={invalidateTask}
                                                            />
                                                        </td>
                                                        )}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={showActionColumn ? 7 : 6} className="px-6 py-10 text-center text-gray-500">
                                                        No subtasks found for you
                                                    </td>
                                                </tr>
                                            )}
                                    </tbody>
                                </table>
                                </div>
                            </div>
                            )}
                        </div>
                    ) : (
                        !isLoading && (
                            <div className="flex flex-col items-center justify-center pt-20">
                                <img
                                    src={noDataIcon}
                                    alt="No Data"
                                    className="w-[15rem] h-[15rem] object-contain opacity-50"
                                />
                                <p className="text-lg text-gray-500 mt-4">Task not found</p>
                                <button
                                    onClick={() => navigate(-1)}
                                    className="mt-4 text-blue-600 hover:underline"
                                >
                                    Go Back
                                </button>
                            </div>
                        )
                    )}
                </div>
            </Layout>
        </>
    );
};

export default OperationsManagementTaskUpdates;
